import { createSlice } from "@reduxjs/toolkit";
import {
  setCategoryError,
  clearCategoryError,
  clearCategoryDetails,
  clearCuratedCategoryTreatments,
} from "./actions";
import {
  categoryDetailsLoaded,
  categoryFaqsLoaded,
  categoryLoaded,
  siblingCategoriesLoaded,
  relatedCategoriesLoaded,
  loadCuratedCategoryTreatments,
} from "./actions/loadCategoryDetails";
import { mapBreadcrumbs } from "./mapBreadcrumbs";

export {
  setCategoryError,
  clearCategoryError,
  clearCuratedCategoryTreatments,
} from "./actions";
export {
  getCategoryDetails,
  getCategoryId,
  getCategoryGender,
  getCategoryTitle,
  getCategoryBreadcrumbs,
  getCategoryError,
  getCategoryHeaderDescription,
  getCategoryFooterDescription,
  getCategoryIsBrand,
  getCategoryIsEarlyAccess,
  getCategoryFaqs,
  getIsEarlyAccessCategoryComingSoon,
  getCategoryTreatments,
  getLoyaltyBenefitType,
  getCategoryVisualCategories,
} from "./selectors";

const initialState = {
  breadcrumbs: [],
};

const hasVisualCategories = (details) =>
  Array.isArray(details?.visualCategories) &&
  details.visualCategories.length > 0;

const isWithinVisualCategoryGroup = ({ currentDetails, nextCategoryId }) => {
  if (!hasVisualCategories(currentDetails) || nextCategoryId == null) {
    return false;
  }

  if (
    currentDetails.visualCategoriesRootCategoryId != null &&
    String(currentDetails.visualCategoriesRootCategoryId) ===
      String(nextCategoryId)
  ) {
    return true;
  }

  return currentDetails.visualCategories.some(
    ({ id }) => String(id) === String(nextCategoryId),
  );
};

const mergeVisualCategoriesIfNeeded = ({
  currentDetails = {},
  nextDetails = {},
}) => {
  const isWithinCurrentVisualCategoryGroup = isWithinVisualCategoryGroup({
    currentDetails,
    nextCategoryId: nextDetails.id,
  });

  if (!isWithinCurrentVisualCategoryGroup) {
    return nextDetails;
  }

  return {
    ...nextDetails,
    visualCategories: hasVisualCategories(nextDetails)
      ? nextDetails.visualCategories
      : currentDetails.visualCategories,
    visualCategoriesRootCategoryId:
      currentDetails.visualCategoriesRootCategoryId,
  };
};

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(categoryDetailsLoaded, (state, action) => {
        if (action.error) {
          return;
        }

        state.details = mergeVisualCategoriesIfNeeded({
          currentDetails: state.details,
          nextDetails: action.payload,
        });
      })
      .addCase(categoryFaqsLoaded, (state, action) => {
        if (action.error) {
          return;
        }

        state.faqs = action.payload;
      })
      .addCase(categoryLoaded, (state, action) => {
        if (action.error) {
          return;
        }

        state.isBrand = action.payload.isBrand;
        state.gender = action.payload.gender;
        state.type = action.payload.type;
        state.childLinks = action.payload.categories;
        state.breadcrumbs = mapBreadcrumbs(action.payload.breadcrumb);
        state.isCurated = action.payload.isCurated;
        state.loyaltyBenefitType = action.payload.loyaltyBenefitType;
      })
      .addCase(siblingCategoriesLoaded, (state, action) => {
        state.siblingCategories = action.payload || [];
      })
      .addCase(relatedCategoriesLoaded, (state, action) => {
        state.relatedCategories = action.payload || [];
      })
      .addCase(loadCuratedCategoryTreatments.fulfilled, (state, action) => {
        state.treatments = action.payload;
      })
      .addCase(clearCategoryDetails, (state) => {
        state.details = {};
      })
      .addCase(setCategoryError, (state, action) => {
        const { status, message = "" } = action.payload;

        state.error = {
          status,
          isTimeout: message.includes("timeout"),
        };
      })
      .addCase(clearCategoryError, (state) => {
        state.error = null;
      })
      .addCase(clearCuratedCategoryTreatments, (state) => {
        state.treatments = null;
      });
  },
});

export default categorySlice.reducer;
